import React, { useEffect, useMemo, useState } from "react";

import { Compass } from "lucide-react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import CadenceCard, { CadencePlaybook } from "./CadenceCard";
import CadenceMapModal from "./CadenceMapModal";
import {
  CADENCE_BADGE_STYLES,
  CADENCE_ORDER,
  CadenceKey,
  resolveCadenceLabel,
} from "./constants";
import PageHeader from "../../Components/PageHeader";
import TabsComponent from "../../Components/Tab";
import { useAppSelector } from "../../Store/Hooks";
import { isRole, ROLES } from "../../Utils/Constants/RoleConstants";
import { getTodayCadence } from "../../Utils/Guidance/guidanceCadence";
import { openGuidanceFloat } from "../../Utils/Guidance/guidanceDrawerBus";
import {
  getVisibleGuidanceRoles,
  isGuidanceHubEnabledForRole,
} from "../../Utils/Guidance/guidanceHubSettings";
import { resolveGuidanceLinks } from "../../Utils/Guidance/guidanceLinkResolver";
import {
  buildRhythmRoleView,
  resolveGuidanceRoleLabel,
} from "../../Utils/Guidance/guidanceRhythm";
import {
  GUIDANCE_ROLES,
  GuidanceRole,
  getDefaultGuidanceRole,
} from "../../Utils/Guidance/guidanceRole";
import { getAccessibleLinkKeysForRole } from "../../Utils/Guidance/guidanceRoleAccess";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";

const isValidPersona = (value: string | null): value is GuidanceRole =>
  !!value && GUIDANCE_ROLES.some(p => p.key === value);

const isValidCadence = (value: string | null): value is CadenceKey =>
  !!value && CADENCE_ORDER.includes(value as CadenceKey);

const resolveOpenCadence = (
  fromQuery: string | null,
  fallback: CadenceKey
): CadenceKey => (isValidCadence(fromQuery) ? fromQuery : fallback);

const dtoToPlaybook = (live: {
  heading: string | null;
  time: string | null;
  nav: string | null;
  what: string[];
  lookfor: string[];
  links: string[];
  agenda?: Array<Record<string, unknown>> | null;
}): CadencePlaybook => ({
  heading: live.heading || "",
  time: live.time || "",
  nav: live.nav || "",
  what: live.what || [],
  lookfor: live.lookfor || [],
  links: live.links || [],
  agenda: (live.agenda as CadencePlaybook["agenda"]) ?? undefined,
});

const GuidancePlaybooks: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const { user } = useAppSelector(state => state.authentication);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const userRole = user?.user?.role || loggedInUser?.role;

  const withOrg = (path: string) => {
    const p = path.startsWith("/") ? path : `/${path}`;
    return orgSlug ? `/${orgSlug}${p}` : p;
  };

  const accessibleLinkKeys = useMemo(
    () => getAccessibleLinkKeysForRole(userRole),
    [userRole]
  );

  const {
    navLinks,
    playbookLinks,
    playbookLinksLoading,
    playbookLinksError,
    rhythmProfiles,
    rhythmCadences,
    rhythmLoading,
    cadenceSchedule,
    hubEnabled,
    hubEnabledRoles,
    previewDayOverride,
    previewFirstWeekOverride,
    previewFirstQuarterOverride,
  } = useAppSelector(state => state.guidance);

  const hubClosed =
    !isRole(userRole, ROLES.VENDOR_ADMIN) &&
    (!hubEnabled || !isGuidanceHubEnabledForRole(userRole, hubEnabledRoles));

  const visibleRoles = useMemo(() => {
    if (isRole(userRole, ROLES.VENDOR_ADMIN)) {
      return GUIDANCE_ROLES.map(p => p.key);
    }
    const visible = getVisibleGuidanceRoles(hubEnabledRoles);
    return visible.length ? visible : GUIDANCE_ROLES.map(p => p.key);
  }, [userRole, hubEnabledRoles]);

  const [role, setPersona] = useState<GuidanceRole>(() => {
    const fromQuery = searchParams.get("role");
    return isValidPersona(fromQuery)
      ? fromQuery
      : getDefaultGuidanceRole(userRole);
  });

  const [expandAll, setExpandAll] = useState(false);

  const todayCadence = useMemo(
    () =>
      getTodayCadence(
        undefined,
        cadenceSchedule,
        previewDayOverride,
        previewFirstWeekOverride,
        previewFirstQuarterOverride
      ),
    [
      cadenceSchedule,
      previewDayOverride,
      previewFirstWeekOverride,
      previewFirstQuarterOverride,
    ]
  );

  const [openCadence, setOpenCadence] = useState<CadenceKey | null>(() => {
    const fromQuery = searchParams.get("cadence");
    const initialToday = getTodayCadence(
      undefined,
      cadenceSchedule,
      previewDayOverride,
      previewFirstWeekOverride,
      previewFirstQuarterOverride
    );
    return resolveOpenCadence(fromQuery, initialToday);
  });

  const [cadenceMapOpen, setCadenceMapOpen] = useState(
    () => searchParams.get("openRhythm") === "1"
  );

  useEffect(() => {
    const personaFromQuery = searchParams.get("role");
    setPersona(
      isValidPersona(personaFromQuery)
        ? personaFromQuery
        : getDefaultGuidanceRole(userRole)
    );
    const cadenceFromQuery = searchParams.get("cadence");
    setOpenCadence(resolveOpenCadence(cadenceFromQuery, todayCadence));
    setExpandAll(false);
    setCadenceMapOpen(searchParams.get("openRhythm") === "1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    if (!visibleRoles.includes(role)) {
      setPersona(visibleRoles[0]);
    }
  }, [visibleRoles, role]);

  const personaContent = useMemo(
    () =>
      buildRhythmRoleView(role, rhythmProfiles, rhythmCadences, playbookLinks),
    [role, rhythmProfiles, rhythmCadences, playbookLinks]
  );

  const rolePlaybooks = useMemo(
    () => playbookLinks.filter(p => p.role === role),
    [playbookLinks, role]
  );

  const personaCadenceOrder = personaContent.cadences.length
    ? personaContent.cadences.map(c => c.key as CadenceKey)
    : CADENCE_ORDER;

  const isLoading = playbookLinksLoading || rhythmLoading;
  const hasRoleContent = rolePlaybooks.length > 0;

  const cadenceLabel = (cadenceKey: CadenceKey): string => {
    const playbook = playbookLinks.find(
      p => p.role === role && p.cadence === cadenceKey
    );
    return resolveCadenceLabel(playbook?.cadence_tag, cadenceKey);
  };

  const cadenceTime = (cadenceKey: CadenceKey): string | undefined => {
    const rhythmTime = personaContent.cadences.find(
      c => c.key === cadenceKey
    )?.time;
    const playbookTime = playbookLinks.find(
      p => p.role === role && p.cadence === cadenceKey
    )?.time;
    const time = rhythmTime || playbookTime || undefined;
    if (!time) return time;
    const durationOnly = time.match(
      /^[\d]+(?:[\s–-]+[\d]+)?\s*(?:min|mins|hrs?|hours?)\b/i
    );
    return durationOnly ? durationOnly[0] : time;
  };

  const scrollToCadence = (cadenceKey: CadenceKey) => {
    setExpandAll(false);
    setOpenCadence(cadenceKey);
    requestAnimationFrame(() => {
      document
        .getElementById(`guidance-cadence-card-${cadenceKey}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleCardToggle = (cadenceKey: CadenceKey) => {
    if (expandAll) {
      setExpandAll(false);
      setOpenCadence(cadenceKey);
      return;
    }
    setOpenCadence(prev => (prev === cadenceKey ? null : cadenceKey));
  };

  const handleToggleExpandAll = () => {
    setExpandAll(prev => {
      const next = !prev;
      setOpenCadence(next ? null : todayCadence);
      return next;
    });
  };

  const handleRoleTabChange = (id: GuidanceRole) => {
    setPersona(id);
    setExpandAll(false);
    setOpenCadence(todayCadence);
  };

  const isCardOpen = (cadenceKey: CadenceKey) =>
    expandAll || openCadence === cadenceKey;

  if (hubClosed) {
    return (
      <div className="bg-white overflow-hidden mt-3 guidance-hub-page">
        <PageHeader
          title="Playbooks Hub"
          subTitle="Playbooks tailored to your role and cadence."
          icon={Compass}
        />
        <div className="w-full bg-white p-3 md:p-4">
          <div className="rounded-md bg-gray-100 p-8 text-center">
            <div className="text-sm font-semibold text-gray-900">
              Playbooks Hub isn&apos;t available for your role
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              Ask your ExecBridge administrator if you need access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white overflow-hidden mt-3 guidance-hub-page">
      <PageHeader
        title="Playbooks Hub"
        subTitle="Playbooks tailored to your role and cadence."
        icon={Compass}
      />
      <div className="w-full bg-white p-3 md:p-4">
        <div className="rounded-md bg-gray-100 p-4 md:p-6">
          <div>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <TabsComponent
                tabs={GUIDANCE_ROLES.filter(p =>
                  visibleRoles.includes(p.key)
                ).map(p => ({
                  id: p.key,
                  label: resolveGuidanceRoleLabel(p.key, rhythmProfiles),
                }))}
                activeTab={role}
                variant="tab-segmented"
                onTabChange={handleRoleTabChange}
              />

              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                    View
                  </span>
                  <button
                    type="button"
                    onClick={handleToggleExpandAll}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${
                      expandAll
                        ? "border-purple-300 bg-purple-50 text-purple-700"
                        : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    {expandAll ? "✕ Collapse All" : "≡ Expand All"}
                  </button>
                </div>
              </div>
            </div>

            {isLoading && (
              <div className="mb-3.5 rounded-[10px] border border-gray-200 bg-white px-4 py-6 text-sm text-gray-500 text-center">
                Loading playbooks…
              </div>
            )}

            {!isLoading && playbookLinksError && (
              <div className="mb-3.5 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                Couldn&apos;t load playbooks: {playbookLinksError}
              </div>
            )}

            {!isLoading && !playbookLinksError && !hasRoleContent && (
              <div className="mb-3.5 rounded-[10px] border border-gray-200 bg-white px-4 py-6 text-sm text-gray-500 text-center">
                No playbook content yet for this role.
              </div>
            )}

            {!isLoading && hasRoleContent && (
              <>
                <div className="flex items-center flex-wrap gap-2 px-3.5 py-2.5 bg-[#f8fafc] border border-gray-200 rounded-[10px] mb-3.5">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 whitespace-nowrap mr-0.5">
                    Your Rhythm
                  </span>
                  {personaCadenceOrder.map(cadenceKey => {
                    const style = CADENCE_BADGE_STYLES[cadenceKey];
                    const isActive = expandAll || openCadence === cadenceKey;
                    return (
                      <button
                        key={cadenceKey}
                        type="button"
                        onClick={() => scrollToCadence(cadenceKey)}
                        title={`Jump to ${cadenceLabel(cadenceKey)} playbook`}
                        className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-[3px] rounded-full border whitespace-nowrap transition-shadow ${
                          isActive ? "ring-2 ring-offset-1 ring-purple-400" : ""
                        }`}
                        style={{
                          backgroundColor: style.bg,
                          color: style.text,
                          borderColor: style.border,
                        }}
                      >
                        {cadenceLabel(cadenceKey)}
                        {cadenceTime(cadenceKey) && (
                          <span className="text-[11px] opacity-70">
                            {" "}
                            · {cadenceTime(cadenceKey)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                  <span className="flex-1 min-w-2" />
                  <button
                    type="button"
                    onClick={() => setCadenceMapOpen(true)}
                    className="text-xs font-semibold text-[#2563eb] hover:underline whitespace-nowrap"
                  >
                    Rhythm &amp; Outcomes →
                  </button>
                </div>

                <div className="space-y-3">
                  {personaCadenceOrder.map(cadenceKey => {
                    const liveContent = playbookLinks.find(
                      p => p.role === role && p.cadence === cadenceKey
                    );
                    if (!liveContent) return null;
                    const playbook = dtoToPlaybook(liveContent);
                    return (
                      <CadenceCard
                        key={cadenceKey}
                        cadenceKey={cadenceKey}
                        displayLabel={cadenceLabel(cadenceKey)}
                        playbook={playbook}
                        resolvedLinks={resolveGuidanceLinks(
                          playbook.links,
                          accessibleLinkKeys,
                          navLinks
                        )}
                        isOpen={isCardOpen(cadenceKey)}
                        isToday={cadenceKey === todayCadence}
                        onToggle={() => handleCardToggle(cadenceKey)}
                        onFloat={() =>
                          openGuidanceFloat({ persona: role, cadenceKey })
                        }
                        withOrg={withOrg}
                      />
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <CadenceMapModal
        isOpen={cadenceMapOpen}
        onClose={() => setCadenceMapOpen(false)}
        personaLabel={personaContent.personaLabel}
        personaTagline={personaContent.personaTagline}
        personaIcon=""
        closingHeadline={personaContent.closingHeadline}
        closingBullets={personaContent.closingBullets}
        cadences={personaContent.cadences}
        onSelectCadence={cadenceKey => {
          setCadenceMapOpen(false);
          scrollToCadence(cadenceKey as CadenceKey);
        }}
      />
    </div>
  );
};

export default GuidancePlaybooks;
